import { useLocation } from "react-router";

import { useClientContext } from "~/contexts/client-context";
import type { Action } from "~/core";
import {
  isClickAction,
  isExternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { useGtmContext } from "~/shared/utils/gtm-sdk/provider";
import { isIOSUserAgent } from "~/shared/utils/platform.utils";
import { useAuthModals } from "~/widgets/auth-modals";
import { actionEmitter } from "~/widgets/use-action/action-events";

export const useAction = (): {
  callAction: (action: Action) => Promise<void>;
  isActionInactive: (action: Action) => boolean;
} => {
  const {
    authToken,
    featureFlags,
    playerID,
    amount,
    paymentMethod,
    statusCode,
    transactionId,
    errorMessage,
    windowType,
    language,
    siteSdk,
    setPlayerID,
    userAgent,
  } = useClientContext();
  const { open } = useAuthModals();
  const { gtm } = useGtmContext();

  const { pathname } = useLocation();
  // переход на страницу рума (главную)
  const redirectToRoom = async (): Promise<void> => {
    const { redirectUrl } = await siteSdk.getRoomUrl(language);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const headerAnalytics = {
    clickHeaderMain(triggerUrl: string) {
      const triggerId = triggerUrl.replace(/^\/+|\/+$/g, "");

      gtm.clickHeaderMain({
        eventTime: new Date(),
        playerID,
        triggerId: triggerId === "promotions" ? "Features" : triggerId,
      });
    },
    clickHeaderSub(targetUrl: string) {
      const targetId =
        targetUrl
          .replace(/^\/|\/$/g, "")
          .split("/")
          .at(-1) ?? "";

      gtm.clickHeaderSub({
        eventTime: new Date(),
        playerID,
        targetId,
        // одна из кнопок Хедера в котором кликнули на элемент Выпадающего меню (пока только Features). Аналитика требует название кнопки как на сайте
        triggerId: "Features",
        screenName: "mainPage",
      });
    },
    createAccountHeader() {
      gtm.createAccountHeader({
        eventTime: new Date(),
        playerID,
        screenName: "signUp",
      });
      redirectToRoom();
    },
    loginHeader() {
      gtm.loginHeader({
        eventTime: new Date(),
        playerID,
        screenName: "logIn",
      });
      redirectToRoom();
    },
  };

  const onLogout = async (): Promise<void> => {
    try {
      const res = await fetch("/api/auth/logout", {
        method: "POST",
      });

      if (!res.ok) {
        const responseData = await res.json();
        throw new Error(
          `Ошибка при выходе из аккаунта: ${JSON.stringify(responseData)}`,
        );
      }

      setPlayerID("-1");
      window.location.reload();
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Ошибка выхода: ${error.message}`);
      } else {
        throw new Error(`Ошибка выхода: ${JSON.stringify(error)}`);
      }
    }
  };

  const onCallAction = async (action: Action): Promise<void> => {
    const currentAction = action?.action?.[0];

    if (!currentAction?.item) {
      return;
    }

    if (!window?.location) {
      return;
    }

    const isRegistrationFeatureEnabled = featureFlags.getItem(
      "registration-feature",
    ).enabled;

    actionEmitter.emit("called", { actionId: action.id });

    if (action.gtm_event?.data) {
      gtm.callEvent(action.gtm_event.data, {
        screenName: location.pathname,
        playerID,
        eventTime: new Date(),
        triggerId: pathname,
        isAuthenticated: !!authToken,
        transactionId,
        amount,
        errorMessage,
        paymentMethod,
        statusCode,
        windowType,
      });
    }

    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "signIn"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("login");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "logIn",
        });
      } else {
        headerAnalytics.loginHeader();
      }
    }

    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "signUp"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("register");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "signUp",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }

    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "fastRegistration"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("fastRegistration");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "fastRegistration",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }
    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "valueChainRegistration"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("valueChainRegistration");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "valueChainRegistration",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }
    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "otpRegistration"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("otpRegistration");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "otpRegistration",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }

    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "valueChainLogin"
    ) {
      if (isRegistrationFeatureEnabled) {
        open("valueChainLogin");
        gtm.createAccountHeader({
          eventTime: new Date(),
          playerID,
          screenName: "valueChainLogin",
        });
      } else {
        headerAnalytics.createAccountHeader();
      }
    }
    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "goToLobby"
    ) {
      redirectToRoom();
    }

    if (
      isClickAction(currentAction?.item) &&
      currentAction?.item.type === "signOut"
    ) {
      await onLogout();
    }
    if (
      isClickAction(currentAction?.item) &&
      currentAction.item.type === "openAppDownloadModal"
    ) {
      open("appDownloadModal");
    }
    // если href-темплейт, то вызвать ендпоинт для распарсивания и подстановки переменных
    if (isExternalLink(currentAction?.item)) {
      const href = currentAction?.item?.href ?? "";
      const parsedUrl = isTemplateUrl(currentAction?.item)
        ? await siteSdk.getParsedRoomUrlFromTemplate({
            template: href,
            locale: language,
          })
        : null;

      window.location.href = parsedUrl?.redirectUrl ?? href;
    }
  };

  const isActionInactive = (control: Action): boolean => {
    const isAuth = !!authToken;
    const maxTouchPoints =
      typeof navigator !== "undefined" ? (navigator.maxTouchPoints ?? 0) : 0;
    if (control.inactive_if?.includes("authorized") && isAuth) {
      return true;
    }
    if (control.inactive_if?.includes("unauthorized") && !isAuth) {
      return true;
    }
    if (
      control.inactive_if?.includes("isIOS") &&
      isIOSUserAgent(userAgent, maxTouchPoints)
    ) {
      return true;
    }

    return false;
  };

  return { callAction: onCallAction, isActionInactive };
};
